
import Vue, { VueConstructor } from 'vue'
import WizardWrapper from '@/components/wizard/WizardWrapper.vue'
import { mapActions, mapGetters } from 'vuex'
import { getWizardSteps, WizardStep } from '@/services/onboarding'
import BundleSetupComplete from '@/components/onboarding/OnboardingSetupComplete.vue'

import BundleSelection from '@/components/wizard/BundleSelection.vue'
import BundleAddCard from '@/components/wizard/BundleAddCard.vue'
import { RButton, RLoader } from '@/components'

import MQ from '@/mixins/MediaQuery'
import { openInNewTab, routerPush } from '@/services/routing'
import { BundlePreset } from '@/store/types/config'
import { BundlePeriod } from '@/api/types/user'

type Onboardingdata = {
  name: string
  currentStepIndex: number
  selectedBundle: BundlePreset | null
  finalizeInProgress: boolean
  wizardSteps: WizardStep[]
  done: boolean
  bundlePeriod: BundlePeriod
}

export default (Vue as VueConstructor<Vue & InstanceType<typeof MQ>>).extend({
  name: 'Onboarding',
  components: {
    WizardWrapper,
    BundleSetupComplete,
    BundleSelection,
    RButton,
    BundleAddCard,
    RLoader,
  },
  mixins: [MQ],
  data(): Onboardingdata {
    return {
      name: '',
      currentStepIndex: 0,
      selectedBundle: null,
      finalizeInProgress: false,
      wizardSteps: [],
      done: false,
      bundlePeriod: 'month',
    }
  },
  created() {
    // This is a hack to reset payment module
    this.$store.commit('payment/setIsPaymentComplete', false)
    this.$store.commit('payment/setIsPaymentValid', false)

    this.wizardSteps = getWizardSteps(this.hasPaymentMethod)
  },
  computed: {
    currentStep(): string {
      return this.wizardSteps[this.currentStepIndex].step
    },
    ...mapGetters({
      isPaymentComplete: 'payment/isPaymentComplete',
      selectableBundles: 'config/selectableBundles',
      setupRequest: 'user/setupRequest',
      hasPaymentMethod: 'user/hasPaymentMethod',
      isRegionDisabled: 'config/isRegionDisabled',
      getBundleRequestPending: 'config/getConfigRequestPending',
    }),
    variant(): string {
      return this.mq === 'sm' ? 'mobile' : 'desktop'
    },
    // NOT IMPLEMENTED
    slideDir(): string {
      return 'right'
    },
    shouldHavePaymentStep(): boolean {
      return !this.hasPaymentMethod
    },
    shouldShowBundles(): boolean {
      return this.selectableBundles && this.selectableBundles.length && !this.isRegionDisabled
    },
    allowTrial(): boolean {
      // a single point of control for vuex action allowTrial argument
      return true
    },
  },
  methods: {
    handleSelectBundle({
      bundle,
      bundlePeriod,
    }: {
      bundle: BundlePreset
      bundlePeriod: BundlePeriod
    }): void {
      this.selectedBundle = bundle
      this.bundlePeriod = bundlePeriod
      // setup immiadetely or move to payment step
      if (!this.shouldHavePaymentStep && this.ensureCodename()) {
        const { codename } = this.selectedBundle
        this.setup({ codename, bundlePeriod, allowTrial: this.allowTrial })
      } else {
        this.currentStepIndex++
      }
    },
    handleContact(): void {
      openInNewTab('https://meetings-eu1.hubspot.com/michael-zyczkowski/talk-to-sales-renderro')
    },
    handleSkip(): void {
      localStorage.setItem('skipSetup', 'true')
      routerPush(this.$router, '/dashboard')
    },
    changeBunldleAfterRedirect(redirectedBundle: BundlePreset): void {
      this.selectedBundle = redirectedBundle
    },

    async finalize(): Promise<void> {
      if (this.ensureCodename()) {
        const { bundlePeriod, allowTrial } = this
        const { codename } = this.selectedBundle! // we "bang" with non-null/non-undefined operator
        // becuase the type checker is unable to conclude that the existance check happens elsewhere, in ensureCodename

        // this is a kind of EventBus implementation to communicate with a child,
        // that in turn uses the neccessary stripe data to call a payment module action.
        this.$root.$emit('save-card', {
          codename,
          bundlePeriod,
          allowTrial,
        })
      }
    },

    ensureCodename(): boolean {
      const { selectedBundle } = this
      if (!selectedBundle || !selectedBundle.codename) {
        this.error('Something went wrong during the setup. Please try again later.')
        return false
      }
      return true
    },

    ...mapActions({
      setup: 'user/setup',
      error: 'notification/error',
    }),
  },
  watch: {
    setupRequest(newVal) {
      if (newVal.successful) this.done = true
    },
  },
})
